import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@wbk/ui';
import { WebSection } from '@wbk/contentful/api';
import { ContentfulMedia, parseSectionItem } from '@wbk/contentful';
import { Link, useRouter } from '@wbk/config';
import { useWindowResize } from '@wbk/hooks';
import { formatCustomDate } from '@/utils';
import useZonesContext from '@/context/zones/useZones';
import EventCard from '@/components/events/EventCard';
import ScrollTitle from '@/components/ui/ScrollTitle';
import { getEventRedirectUrl } from '@/utils/redirect';

type Props = {
  section: WebSection;
};

export default function Discover({ section }: Props) {
  //   const [items, setItems] = useState(section?.contentCollection?.items.slice(0, 4) || []);
  const { locale } = useRouter();
  const { targetRef } = useZonesContext();
  const { t } = useTranslation(['common']);
  const items = section.contentCollection.items;

  const { selectedAnchorCard } = useZonesContext();
  const activeCard = selectedAnchorCard
    ? parseSectionItem({ item: selectedAnchorCard, locale })
    : null;

  const [localItems, setLocalItems] = useState(items);

  const { width } = useWindowResize();
  useEffect(() => {
    if (width < 640) {
      setLocalItems(items.slice(0, 4));
      return;
    }

    setLocalItems(items?.slice(0, 6));
  }, [width, items]);

  function handleLoadMore() {
    const currentLength = localItems.length;
    const moreItems = items.slice(currentLength, currentLength + 4);
    setLocalItems([...localItems, ...moreItems]);
  }

  return (
    <div className='container-padding container'>
      <section ref={targetRef} className='container px-3 xl:px-0'>
        <div className='flex flex-col gap-10'>
          <ScrollTitle title={section?.title} className='max-w-xs md:max-w-sm' />

          <div className='grid grid-cols-2 gap-4 lg:grid-cols-4'>
            {activeCard && (
              <div className='relative col-span-2 h-40 lg:col-span-1 lg:row-span-2 lg:h-auto'>
                <ContentfulMedia
                  media={activeCard?.image11}
                  className='h-full w-full rounded-2xl object-cover transition-transform duration-300 ease-in-out group-hover:scale-110'
                />

                <div className='absolute inset-0 rounded-3xl bg-black/20 transition-opacity duration-300' />
                <div className='absolute inset-0 flex flex-col items-center justify-center gap-1 p-2 backdrop-blur-[1px] sm:p-4'>
                  <ContentfulMedia
                    media={activeCard?.image31}
                    className='z-10 w-36 object-cover transition-transform duration-300 ease-in-out group-hover:scale-110 md:w-52'
                  />
                </div>
              </div>
            )}
            <>
              {localItems.map((item, index) => {
                const parsedItem = parseSectionItem({ item, locale });
                if (!parsedItem) return null;
                const parsed = parsedItem;

                const redirectInfo = getEventRedirectUrl(
                  item,
                  'slug' in item ? item.slug : '',
                  locale,
                  parsed
                );

                // @ts-expect-error -- schedule is available but not typed
                const openDate = parsed?.schedule?.openDateTime;

                return (
                  <Button
                    as={Link}
                    href={redirectInfo.href}
                    theme='white'
                    shape='text'
                    className='first:cols-span-2 w-full p-0 hover:bg-transparent'
                    key={index}
                    target={redirectInfo.isExternal ? '_blank' : undefined}
                    rel='noreferrer'
                  >
                    <EventCard
                      key={index}
                      eventType={parsedItem?.eventType}
                      venueName={parsed.title}
                      eventDate={openDate ? formatCustomDate(new Date(openDate), locale) : ''}
                      priceFrom={parsed.startingPrice}
                      currency={parsed.currencyCode}
                      image={parsed.image11 || parsed?.image31}
                      // @ts-expect-error -- zone object exists by not typed
                      zoneLogo={parsed?.zone?.zoneLogo}
                      className='w-full'
                      buttonText={parsed?.label || undefined}
                    />
                  </Button>
                );
              })}
            </>
          </div>
          {localItems.length < items.length && (
            <div className='flex w-full items-center justify-center'>
              <Button
                onClick={handleLoadMore}
                className='rounded-full border border-white bg-transparent px-4 transition-all duration-200 hover:bg-white/10 md:px-8'
              >
                {t('loadMore')}
              </Button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
